import { useEffect, useRef } from "react";

import { GetWSMgr } from "@/Utils/StateLib";
import { useRouteState } from "@/hooks/useRouteState";
import { useTopic } from "@/hooks/useTopic";
import { useChartDocumentContext } from "@/providers/ChartDocumentProvider";

/**
 * Syncs ?levels= URL param into coreapp's doc.SetRequestedLevels +
 * SubChartSwitch. Direction is URL → coreapp; the menu writes URL via
 * setRouteParam("levels", value), this hook drives coreapp from the URL.
 *
 * De-dupes via a (doc, value) ref so the same levels never re-fire for
 * the same doc — guards against re-renders that don't represent a real
 * change. Re-runs on chartDocument update so cold-start ?levels=N is
 * applied once the chart's subchart manager is ready.
 *
 * Also listens for "PageRefused": a level can be applied and then rejected at render
 * time (too many records, or an extent past what the browser will draw), and without
 * this the URL and the levels menu keep advertising a page that never appeared.
 * coreapp has already restored its own state by then, so writing the restored value
 * back re-enters the effect below and no-ops on the GetLoadedLevels check.
 */
export const useLevelsSync = () => {
  const { levels: levelsParam, setParam } = useRouteState();
  const { chartDocument } = useChartDocumentContext() ?? {};
  const lastAppliedRef = useRef<{ doc: unknown; value: string } | null>(null);

  useTopic<{ levels: number | null } | undefined>("PageRefused", data => {
    const restored = data?.levels;
    // leave the de-dupe record so the still-refused level doesn't get
    // reapplied on the next unrelated re-render.
    if (restored == null) return;
    // Clear the de-dupe so the refused value can be chosen again and re-report
    // instead of silently doing nothing.
    lastAppliedRef.current = null;
    setParam("levels", String(restored));
  });

  useEffect(() => {
    if (levelsParam == null) return;
    const wsMgr = GetWSMgr();
    if (!wsMgr) return;
    const doc = wsMgr.GetDoc();
    if (!doc) return;
    const scm = doc.GetSubChartMgr?.();
    const subchart = scm?.GetCurrentSubChart?.();
    if (!subchart) return;

    const prev = lastAppliedRef.current;
    if (prev && prev.doc === doc && prev.value === levelsParam) return;

    const numeric = parseInt(levelsParam, 10);
    if (!Number.isFinite(numeric)) return;
    if (doc.GetLoadedLevels?.() === numeric) {
      lastAppliedRef.current = { doc, value: levelsParam };
      return;
    }
    lastAppliedRef.current = { doc, value: levelsParam };
    doc.SetRequestedLevels?.(numeric);
    doc.SubChartSwitch?.(subchart);
  }, [levelsParam, chartDocument]);
};
